import { type ChangeEvent, type ForwardedRef, type ReactNode, forwardRef, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import type { Option } from '../../Components/SelectionList/index.ts';
import { SortOrder, ITEM_MIN_HEIGHT_WITH_BORDER, ITEM_MIN_HEIGHT } from '../../Components/SelectionList/index.ts';
import { List, Search } from './components/index.ts';
import {
  DEFAULT_LOAD_DEBOUNCE,
  DEFAULT_OVERFLOW_LIMIT,
  DEFAULT_SEARCH_LIMIT,
  INLINE_VALUE_DELIMITER,
  DEFAULT_TRANSLATIONS,
  COMMA,
} from './constants.ts';
import {
  filterOptionsByString,
  areOptionsEqual,
  sortOptions,
  createOptionValueSet,
  filterOptionsExcluding,
  getOptionText,
} from './helpers.ts';
import { StyledTextarea, Wrapper } from './styled.ts';
import type { MultiSelectionProps, Translations } from './types.ts';

type MultiSelectionComponentType = <T>(
  props: MultiSelectionProps<T> & {
    ref?: ForwardedRef<HTMLDivElement>;
  },
) => ReactNode;

export const MultiSelection = memo(
  forwardRef(function MultiSelection<T>(
    {
      selectedValue,
      onChange,
      options: staticOptions = [],
      loadOptions,
      loadOptionsLimit = DEFAULT_SEARCH_LIMIT,
      isLoading,
      disabled,
      error,
      initialSelectionSortOrder = SortOrder.ASC,
      initialSelectedSortOrder = SortOrder.ASC,
      overflowLimit = DEFAULT_OVERFLOW_LIMIT,
      getOptionKey,
      formatOptionLabel,
      getOptionText: getOptionTextFn,
      translations,
      hideInlineToggle = false,
      hideBulkSelect = false,
      hideBulkDeselect = false,
      maxItems,
      reverseSelectionMode = false,
      dataTestId,
    }: MultiSelectionProps<T>,
    ref: ForwardedRef<HTMLDivElement>,
  ) {
    const [fetchedOptions, setFetchedOptions] = useState<Option<T>[]>([]);
    const [internalLoading, setInternalLoading] = useState<boolean>(!!loadOptions);

    const [selectionFilter, setSelectionFilter] = useState<string>('');
    const [selectedFilter, setSelectedFilter] = useState<string>('');

    const [isInlineMode, setIsInlineMode] = useState<boolean>(false);
    const [inlineValue, setInlineValue] = useState<string>('');

    const [selectionSortOrder, setSelectionSortOrder] = useState<SortOrder>(initialSelectionSortOrder);
    const [selectedSortOrder, setSelectedSortOrder] = useState<SortOrder>(initialSelectedSortOrder);

    const selectionListRef = useRef<HTMLDivElement>(null);
    const [textareaHeight, setTextareaHeight] = useState<number>(0);
    const listContainerHeight = useMemo(() => ITEM_MIN_HEIGHT_WITH_BORDER * overflowLimit + ITEM_MIN_HEIGHT / 2, [overflowLimit]);

    const mergedTranslations = useMemo<Required<Translations>>(
      () => ({
        ...DEFAULT_TRANSLATIONS,
        ...translations,
      }),
      [translations],
    );

    const hasLoadOptions = useMemo(() => !!loadOptions, [loadOptions]);
    const options = useMemo(() => (hasLoadOptions ? fetchedOptions : staticOptions), [fetchedOptions, staticOptions, hasLoadOptions]);

    const isSelectionFilterDisabled = useMemo(
      () => (hasLoadOptions ? disabled : isLoading || disabled),
      [hasLoadOptions, isLoading, disabled],
    );

    const availableSelectionItems = useMemo(() => {
      const selectedValueSet = createOptionValueSet(selectedValue ?? []);
      return filterOptionsExcluding(options, selectedValueSet);
    }, [options, selectedValue]);

    const filteredSelectionItems = useMemo(() => {
      if (isInlineMode || hasLoadOptions) {
        // For async options, don't apply client-side sorting - server handles it
        return availableSelectionItems;
      }
      const filteredItems = filterOptionsByString(availableSelectionItems, selectionFilter, formatOptionLabel, getOptionTextFn);
      return sortOptions(filteredItems, selectionSortOrder, formatOptionLabel, getOptionTextFn);
    }, [isInlineMode, hasLoadOptions, availableSelectionItems, selectionFilter, formatOptionLabel, selectionSortOrder, getOptionTextFn]);

    const selectableSelectionItems = useMemo(() => filteredSelectionItems.filter((option) => !option.disabled), [filteredSelectionItems]);

    const filteredSelectedItems = useMemo(() => {
      const filteredItems = filterOptionsByString(selectedValue, selectedFilter, formatOptionLabel, getOptionTextFn);
      return sortOptions(filteredItems, selectedSortOrder, formatOptionLabel, getOptionTextFn);
    }, [selectedValue, selectedFilter, formatOptionLabel, selectedSortOrder, getOptionTextFn]);

    const loadOptionsDebounced = useDebouncedCallback(async (searchString: string, newSortOrder?: SortOrder) => {
      if (!loadOptions) return;

      setInternalLoading(true);
      try {
        const options = await loadOptions(searchString, loadOptionsLimit.MIN, newSortOrder ?? selectionSortOrder);
        setFetchedOptions(options || []);
      } catch {
        setFetchedOptions([]);
      } finally {
        setInternalLoading(false);
      }
    }, DEFAULT_LOAD_DEBOUNCE);

    const handleSelectionFilterChange = useCallbackRef((value: string) => {
      setSelectionFilter(value);
      if (hasLoadOptions) {
        loadOptionsDebounced(value);
      }
    });

    const handleSelectedFilterChange = useCallbackRef((value: string) => {
      setSelectedFilter(value);
    });

    const clearSelectionFilter = useCallbackRef(() => {
      setSelectionFilter('');
      if (hasLoadOptions) {
        loadOptionsDebounced('');
      }
    });

    const clearSelectedFilter = useCallbackRef(() => {
      setSelectedFilter('');
    });

    const toggleInlineMode = useCallbackRef(() => {
      if (!isInlineMode && selectionListRef.current) {
        setTextareaHeight(selectionListRef.current.offsetHeight);
      }
      setIsInlineMode((prev) => !prev);
      setInlineValue(selectedValue.map((option) => getOptionText(option, formatOptionLabel, getOptionTextFn)).join(INLINE_VALUE_DELIMITER));
    });

    const handleChangeStringValue = useCallbackRef((e: ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.currentTarget;

      const formattedMap = new Map<string, Option<T>>();
      const labelMap = new Map<string, Option<T>>();
      const ambiguousLabels = new Set<string>();

      for (const option of options) {
        const formatted = getOptionText(option, formatOptionLabel, getOptionTextFn).trim().toLowerCase();
        formattedMap.set(formatted, option);

        const rawLabel = option.label.trim().toLowerCase();
        if (rawLabel === formatted) continue;

        const existing = labelMap.get(rawLabel);
        if (existing && !areOptionsEqual(existing, option)) {
          ambiguousLabels.add(rawLabel);
          labelMap.delete(rawLabel);
        } else if (!ambiguousLabels.has(rawLabel)) {
          labelMap.set(rawLabel, option);
        }
      }

      const seenValues = new Set<T>();

      const newSelectedOptions = value
        .split(COMMA)
        .map((label) => label.trim())
        .filter((label) => label.length > 0)
        .reduce<Option<T>[]>((acc, label) => {
          const key = label.toLowerCase();
          const option = formattedMap.get(key) ?? labelMap.get(key);
          if (option && !seenValues.has(option.value)) {
            seenValues.add(option.value);
            acc.push(option);
          }
          return acc;
        }, []);

      onChange(newSelectedOptions);
      setInlineValue(value);
    });

    const isMaxReached = maxItems !== undefined && selectedValue.length >= maxItems;

    const selectItem = useCallbackRef((option: Option<T>) => {
      if (isMaxReached) return;
      onChange([...selectedValue, option]);
    });

    const unselectItem = useCallbackRef((option: Option<T>) => {
      const filteredValue = selectedValue.filter((value) => !areOptionsEqual(option, value));
      if (isInlineMode) {
        setInlineValue(
          filteredValue.map((option) => getOptionText(option, formatOptionLabel, getOptionTextFn)).join(INLINE_VALUE_DELIMITER),
        );
      }
      onChange(filteredValue);
    });

    const selectAll = useCallbackRef(() => {
      const itemsToAdd =
        maxItems !== undefined ? selectableSelectionItems.slice(0, maxItems - selectedValue.length) : selectableSelectionItems;
      onChange([...selectedValue, ...itemsToAdd]);
    });

    const unselectAll = useCallbackRef(() => {
      setInlineValue('');
      const itemsOutsideFilter = selectedValue.filter((option) => !filteredSelectedItems.some((value) => areOptionsEqual(option, value)));
      onChange(itemsOutsideFilter);
    });

    const toggleSelectionSortOrder = useCallbackRef(() => {
      setSelectionSortOrder((prev) => {
        const newSortOrder = prev === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
        if (hasLoadOptions) {
          loadOptionsDebounced(selectionFilter, newSortOrder);
        }
        return newSortOrder;
      });
    });

    const toggleSelectedSortOrder = useCallbackRef(() => {
      setSelectedSortOrder((prev) => (prev === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC));
    });

    useEffect(() => {
      if (hasLoadOptions) {
        loadOptionsDebounced('');
      }
    }, [loadOptionsDebounced, hasLoadOptions]);

    return (
      <Wrapper ref={ref} data-testid={dataTestId}>
        <div>
          <Search
            mode='selection'
            onDebouncedChange={handleSelectionFilterChange}
            onClear={clearSelectionFilter}
            disabled={isSelectionFilterDisabled}
            isInlineMode={isInlineMode}
            toggleInlineMode={toggleInlineMode}
            hideInlineToggle={hideInlineToggle}
            disableInlineToggle={disabled}
            translations={mergedTranslations}
            hasLoadOptions={hasLoadOptions}
          />
          {isInlineMode ? (
            <StyledTextarea
              value={inlineValue}
              onChange={handleChangeStringValue}
              height={textareaHeight}
              disabled={disabled}
              data-testid='inline-textarea'
            />
          ) : (
            <List
              mode={reverseSelectionMode ? 'selected' : 'selection'}
              options={filteredSelectionItems}
              total={availableSelectionItems.length}
              singleAction={selectItem}
              bulkAction={selectAll}
              hideBulkActions={hideBulkSelect}
              isLoading={isLoading || internalLoading}
              disabled={disabled || isMaxReached || selectableSelectionItems.length === 0}
              sortOrder={selectionSortOrder}
              toggleSortOrder={toggleSelectionSortOrder}
              containerHeight={listContainerHeight}
              getOptionKey={getOptionKey}
              formatOptionLabel={formatOptionLabel}
              translations={mergedTranslations}
              wrapperRef={selectionListRef}
            />
          )}
        </div>
        <div>
          <Search
            mode='selected'
            onDebouncedChange={handleSelectedFilterChange}
            onClear={clearSelectedFilter}
            disabled={disabled}
            translations={mergedTranslations}
            hasLoadOptions={false}
          />
          <List
            mode={reverseSelectionMode ? 'selection' : 'selected'}
            options={filteredSelectedItems}
            total={selectedValue.length}
            singleAction={unselectItem}
            bulkAction={unselectAll}
            hideBulkActions={hideBulkDeselect}
            disabled={disabled || filteredSelectedItems.length === 0}
            error={error}
            sortOrder={selectedSortOrder}
            toggleSortOrder={toggleSelectedSortOrder}
            containerHeight={listContainerHeight}
            getOptionKey={getOptionKey}
            formatOptionLabel={formatOptionLabel}
            translations={mergedTranslations}
          />
        </div>
      </Wrapper>
    );
  }),
) as MultiSelectionComponentType;
